/**
 * RFQ Invoices Page
 * =================
 * Uses DataDashboard for a full-featured table view.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataDashboard, { Column } from '../../components/common/DataDashboard';
import invoiceApi from '../../services/invoiceApi';
import AttachmentsModal from '../../components/common/AttachmentsModal';

const RFQInvoices: React.FC = () => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20, // Match backend default
        total: 0,
        pages: 1,
        has_next: false,
        has_prev: false
    });

    // Attachments Modal State
    const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
    const [selectedSupplierForAttachments, setSelectedSupplierForAttachments] = useState<{
        rfqId: string;
        supplierId: string;
        supplierName: string;
    } | null>(null);

    const fetchData = useCallback(async (params: any = {}) => {
        setLoading(true);
        setError(null);
        try {
            const page = params.page || 1;
            const limit = params.limit || 20;

            const response = await invoiceApi.getCompletedRFQs(page, limit);

            if (response.success && response.data) {
                setData(response.data.rfqs);
                setPagination({
                    page: response.data.page,
                    limit: response.data.page_size,
                    total: response.data.total,
                    pages: Math.ceil(response.data.total / response.data.page_size),
                    has_next: response.data.has_next,
                    has_prev: response.data.has_prev
                });
            } else {
                setError(response.message || 'Failed to fetching data');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDataFetch = (newParams: any) => {
        fetchData(newParams);
    };

    const handleOpenAttachments = (row: any) => {
        if (!row.selected_supplier) return;

        setSelectedSupplierForAttachments({
            rfqId: row.id,  // Backend serializes _id as id via Pydantic
            supplierId: row.selected_supplier,
            supplierName: row.selected_supplier_name || 'Supplier'
        });
        setIsAttachmentsModalOpen(true);
    };

    const handleCloseAttachments = () => {
        setIsAttachmentsModalOpen(false);
        setSelectedSupplierForAttachments(null);
    };

    // Column definitions
    const columns: Column[] = useMemo(() => [
        {
            key: 'company_name',
            title: 'Company',
            type: 'text',
            sortable: true,
            width: '150px'
        },
        {
            key: 'product_name',
            title: 'Product',
            type: 'text',
            sortable: true,
            width: '200px'
        },
        {
            key: 'selected_supplier_name',
            title: 'Supplier',
            type: 'text',
            sortable: true,
            width: '150px'
        },
        {
            key: 'completed_at',
            title: 'Completion Date',
            type: 'date',
            sortable: true,
            width: '120px'
        },
        {
            key: 'quantity',
            title: 'Quantity',
            type: 'number',
            sortable: true,
            width: '80px',
            render: (value: any, row: any) => `${value} ${row.unit || ''}`
        },
        {
            key: 'quotes',
            title: 'Quotes',
            type: 'action',
            sortable: false,
            width: '170px',
            render: (value: any, row: any) => {
                const count = row.attachment_count || 0;

                if (count === 0) {
                    return <span style={{ color: '#9ca3af', fontSize: '14px' }}>No Attachments</span>;
                }

                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAttachments(row);
                        }}
                        className="view-attachments-btn"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#2563eb',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: '14px'
                        }}
                    >
                        View Attachments ({count})
                    </button>
                );
            }
        },
        {
            key: 'has_invoice',
            title: 'Invoice Status',
            type: 'status',
            sortable: true,
            width: '120px',
            render: (value: any) => {
                const status = value ? 'active' : 'pending';
                const label = value ? 'Uploaded' : 'Missing';
                return <span className={`status-badge status-${status}`}>{label}</span>;
            }
        }
    ], []);

    const handleRowClick = (item: any) => {
        // Navigate or expand
    };

    return (
        <>
            <DataDashboard
                title="RFQ Invoices"
                collection="rfq-invoices"
                data={data}
                columns={columns}
                pageDescription="View and verify invoices from your completed RFQs"
                onDataChange={setData}
                onRowClick={handleRowClick}
                showAddButton={false}
                showEditButton={false}
                showDeleteButton={false}
                showExportButton={false}
                isLoading={loading}
                error={error}
                paginationInfo={pagination}
                onDataFetch={handleDataFetch}
            />
            {selectedSupplierForAttachments && (
                <AttachmentsModal
                    isOpen={isAttachmentsModalOpen}
                    onClose={handleCloseAttachments}
                    rfqId={selectedSupplierForAttachments.rfqId}
                    supplierId={selectedSupplierForAttachments.supplierId}
                    supplierName={selectedSupplierForAttachments.supplierName}
                />
            )}
        </>
    );
};

export default RFQInvoices;
